import { inject, Injectable } from '@angular/core';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { LOCAL_STORAGE_KEYS } from 'src/app/shared/constants/local-storage.constants';
import { RegionDto } from 'src/app/shared/dtos/user.dto';
import { Router } from '@angular/router';
import { getScreenNameFromPath } from 'src/app/core/utils/analytics.utils';
import { AnalyticsEventName } from 'src/app/core/enums/analytics.enum';
import { AnalyticsData } from 'src/app/core/interfaces/analytics.interfaces';
import { convertToSnakeCase } from 'src/app/shared/utils/string.utils';
import { environment } from 'src/environments/environment';

declare let gtag: (command: string, eventName: string, params?: Record<string, unknown>) => void;

/**
 * Service for tracking analytics events using Google Analytics.
 * You should only set `analyticsPageData` properties that are relevant to the current page.
 * Use the `trackEvent` method to log specific events.
 *
 * @example
 * // Set general analytics data for the current context, this should only be set on page load/change
 * analyticsService.analyticsPageData = {
 *   itemName: 'machine',
 *   itemId: '12345'
 * };
 * analyticsService.trackEvent();
 *
 *
 * // Track a specific event
 * analyticsService.trackEvent(AnalyticsEventName.ItemClick, {
 *   itemName: 'notifications',
 *   itemId: 'notifications'
 * });
 *
 * The reason for this separation is to allow setting common properties once and being able to reuse them across multiple events,
 * this also means we don't need to pass properties around using @Input and @Output.
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly router = inject(Router);

  private _analyticsPageData: AnalyticsData = {
    screenName: '',
    itemName: '',
    itemId: '',
    jcbRangeName: '',
    jcbRangeId: '',
    jcbMachineName: '',
    jcbMachineId: '',
    userEmail: '',
    userRegion: '',
    userName: ''
  }

  get analyticsPageData(): AnalyticsData {
    return this._analyticsPageData;
  }

  set analyticsPageData(data) {
    this._analyticsPageData = data;
  }

  trackPageLoad(): void {
    this.trackEvent(AnalyticsEventName.PageLoad);
  }

  trackEvent(eventName: AnalyticsEventName, params?: Partial<AnalyticsData>): void {
    if (typeof gtag !== 'undefined') {
      const currentRegion = this.getCurrentRegion();
      const screenName = getScreenNameFromPath(this.router.url);
      const newParams: Partial<AnalyticsData> = params ? params : this.analyticsPageData;
      const clonedParams = { ...newParams, userRegion: currentRegion, screenName: params?.screenName || screenName };
      const snakeCaseParams = convertToSnakeCase(clonedParams);

      if (environment.enableLogging) {
        console.log(`Analytics service [trackEvent] - ${eventName}: `, snakeCaseParams);
      }
      
      gtag('event', eventName, snakeCaseParams);
    }
  }

  private getCurrentRegion(): string {
    const region = this.localStorageService.getItem<RegionDto>(LOCAL_STORAGE_KEYS.InitialRegion);

    if (region) {
      return region.code;
    }

    return '';
  }
}
